// In-app i18n.
//
// Home-grown on purpose: the app has ~180 strings across two locales, which
// is well under the weight of react-intl / i18next. We get:
//
//   * ``useT()`` — returns a translator ``t(key, vars?)`` bound to the
//     current locale. Missing keys fall back to the English bundle, then
//     to the key itself so never-translated copy is loud but not crashing.
//   * ``useI18n()`` — access to ``locale``, ``setLocale``, and the
//     ``locales`` catalogue (used by ``LocaleSwitcher``).
//   * ``I18nProvider`` — reads ``localStorage['writeon:locale']`` on mount,
//     then ``navigator.language``, then falls back to ``DEFAULT_LOCALE``.
//     Persists choice to storage and updates ``<html lang>``.
//
// String keys are dot-delimited (``analysis.metric.words``) and
// ``{placeholder}`` interpolation is supported. See ``messages.js`` for the
// bundles — adding a language is ``cp MESSAGES.en → MESSAGES.xx`` and
// extending ``LOCALES``.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DEFAULT_LOCALE, LOCALES, MESSAGES } from './messages';
import { getStoredUser, updateMyPreferences } from '../util/auth';
import { logger } from '../util/logger';

const STORAGE_KEY = 'writeon:locale';
const VALID = new Set(LOCALES.map((l) => l.id));

function resolveInitial() {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && VALID.has(stored)) return stored;
  } catch {
    /* ignore */
  }
  const navLang = window.navigator?.language?.split('-')[0];
  if (navLang && VALID.has(navLang)) return navLang;
  return DEFAULT_LOCALE;
}

function interpolate(template, vars) {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : `{${key}}`,
  );
}

// Fallback for missing dot-keys: pluck the last segment, swap
// underscores for spaces, capitalise the first letter. Lets
// long-tail catalogue strings (kinds, options, metadata field
// names) render readably until proper translations land.
function humanizeKey(key) {
  const last = String(key).split('.').pop() || key;
  const spaced = last.replace(/_/g, ' ').trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const I18nContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (key) => key,
  locales: LOCALES,
});

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(resolveInitial);
  // Skip the locale-sync API call on the very first render — the
  // initial value comes from localStorage / nav language, not from
  // a user-driven switch, so PATCH'ing the user row would be wasted
  // traffic. Only subsequent setLocale calls (the LocaleSwitcher)
  // sync to the backend.
  const initialMountRef = useRef(true);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* ignore */
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
    if (initialMountRef.current) {
      initialMountRef.current = false;
      return;
    }
    // Sync to the backend only when we have a stored auth user —
    // otherwise the PATCH 401s and clutters the log. Best-effort:
    // a network failure here doesn't unset the frontend locale,
    // it just means the next transactional email might use the
    // previous language until the user toggles again.
    if (getStoredUser()) {
      updateMyPreferences({ locale }).catch((err) => {
        logger.warn('locale_sync_failed', err?.message);
      });
    }
  }, [locale]);

  const setLocale = useCallback((next) => {
    if (VALID.has(next)) setLocaleState(next);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const bundle = MESSAGES[locale] ?? MESSAGES[DEFAULT_LOCALE];
      const fallback = MESSAGES[DEFAULT_LOCALE];
      const hit = bundle[key] ?? fallback[key];
      if (hit !== undefined) return interpolate(hit, vars);
      // Missing key: previously we returned ``key`` verbatim. For
      // dotted i18n keys that meant the UI rendered raw strings
      // like ``planner.kind.magic_system`` -- the most-reported
      // symptom against the planner because the backend catalogue
      // ships ~100 kind labels via ``label_i18n`` and the bundle
      // can't keep up. Humanise the last segment instead so the
      // UI degrades gracefully ("Magic system") rather than
      // leaking the key. Single-word keys (no dot) still pass
      // through unchanged -- they're typically intentional raw
      // text rather than i18n lookups.
      if (typeof key === 'string' && key.includes('.')) {
        return humanizeKey(key);
      }
      return key;
    },
    [locale],
  );

  const value = useMemo(
    () => ({ locale, setLocale, t, locales: LOCALES }),
    [locale, setLocale, t],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useI18n() {
  return useContext(I18nContext);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useT() {
  return useContext(I18nContext).t;
}
